import {ReactElement, useEffect, useState} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {dehydrate, QueryClient} from '@tanstack/react-query';
import {Container} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {setApiLocale} from '@eksab/api';
import {useTranslate} from '@eksab/i18n';
import {useAccessToken, useRegionSettings, type RegionSettings} from '@eksab/hooks';
import {Tabs} from '@eksab/components';

import {Header, SponsorImage} from '@eksab/features/competition/components';
import {RankingList, StatusBar} from '@eksab/features/competition/ranking';
import {useRanksQuery, useCompetitionQuery, useSeasonGamesQuery} from '@eksab/features/competition/hooks';
import {Competition, CompetitionType, Participant} from '@eksab/features/competition/types';
import {ParticipantPredictionsModal} from '@eksab/features/competition/ranking/ParticipantPredictions';
import {type RankingType, rankingTypes} from '@eksab/features/competition/ranking/const';

export default function Ranking() {
  const slug = useRouter().query.slug as string;
  const [activeParticipant, setActiveParticipant] = useState<Participant | null>(null);
  const [rankingType, setRankingType] = useState<RankingType>('all');

  const t = useTranslate();
  const isUser = useAccessToken().isSuccess;
  const {data: {id, name = t('app.competition'), sponsors} = {}} = useCompetitionQuery(slug);
  useSeasonGamesQuery({season: id!}, 'viewer', {enabled: !!id});

  useEffect(() => {
    mixpanel?.track('Page View', {page: 'Competition', name, tab: 'Ranking', type: rankingType});
  }, [name, rankingType]);

  return (
    <>
      <Head>
        <meta property="og:title" content={`${name} - ${t('competition.ranking')} | ${t('app.name')}`} />
        <title>{`${name} - ${t('competition.ranking')} | ${t('app.name')}`}</title>
      </Head>

      <StatusBar />

      <Container flexDir="column" mx="auto" py={py}>
        <SponsorImage sponsors={sponsors!} />

        {isUser && (
          <Tabs mb={[4, 6, 8]} alignSelf={['center', null, 'flex-start']}>
            {rankingTypes.map((type) => (
              <Tabs.Item
                key={type}
                title={`competition.ranking.${type}`}
                isActive={type === rankingType}
                onClick={() => setRankingType(type)}
              />
            ))}
          </Tabs>
        )}

        <RankingList type={rankingType} onParticipantClick={isUser ? setActiveParticipant : undefined} />
      </Container>

      <ParticipantPredictionsModal
        isOpen={!!activeParticipant}
        onClose={() => setActiveParticipant(null)}
        participant={activeParticipant}
      />
    </>
  );
}

export const makeGetStaticProps: (route: 'group-competition' | 'eksab-competition') => GetStaticProps =
  (route) =>
  async ({locale, params}) => {
    const queryClient = new QueryClient();
    try {
      setApiLocale(locale);
      const competition = await queryClient.fetchQuery<Competition>(
        [...useCompetitionQuery.queryKey(params!.slug as string), locale],
        () => useCompetitionQuery.queryFn(params!.slug as string, false),
      );

      const regionSettings = await queryClient.fetchQuery<RegionSettings>(
        [useRegionSettings.queryKey, locale],
        useRegionSettings.queryFn,
      );

      if (route === 'group-competition' && regionSettings.settings.show_groups === false)
        return {notFound: true, revalidate: 1};

      const isWrongRoute =
        (route === 'eksab-competition' && competition.creator !== null) ||
        (route === 'group-competition' && competition.creator === null) ||
        competition.type !== CompetitionType.PREDICTION ||
        !regionSettings?.settings?.tactic_enabled;

      if (isWrongRoute) return {notFound: true, revalidate: 1};

      await queryClient.prefetchInfiniteQuery(useRanksQuery.queryKey({seasonId: competition.season.id}), () =>
        useRanksQuery.queryFn({seasonId: competition.season.id}),
      );

      return {
        props: {
          dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        },
      };
    } catch (e) {
      return {notFound: true, revalidate: 1};
    }
  };

export const getStaticProps: GetStaticProps = makeGetStaticProps('eksab-competition');

export function getStaticPaths() {
  return {paths: [], fallback: 'blocking'};
}

Ranking.getLayout = (page: ReactElement) => {
  return (
    <>
      <Header competitionType="predictions" />
      {page}
    </>
  );
};
